/**
 * GameModule das den Lebenszyklus aller sichtbaren Sonnen verwaltet.
 * Berechnet Zustand, Alter, Skalierung. Keine Allokation im Game Loop.
 * Emittiert 'sun:lifecycle-update' nur bei State/Scale-Änderungen.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { SunLifecycleConfig } from '../config/SunLifecycleConfig';
import type { ClusterContent } from '../network/ContentLoader';
import { SunState, type SunFate, type SunLifecycleEntry, type SunRegistration } from './SunLifecycleTypes';
import { calcLifecycleScale } from './SunLifecycleScale';
import { seedToDestructionTime, seedToFate } from './SunLifecycleSeed';
import { SunLifecycleDebug } from './SunLifecycleDebug';

interface RawEntry {
  destructionTime: number;
  fate: SunFate;
  totalLifetimeSec: number;
}

export class SunLifecycle implements GameModule {
  readonly name = 'SunLifecycle';

  private _config: SunLifecycleConfig;
  private _worldCreatedAt: number;
  private _entries = new Map<string, SunLifecycleEntry>();
  private _raw = new Map<string, RawEntry>();
  private _engine: Engine | null = null;
  private _debug = new SunLifecycleDebug();
  private _lastContents: ClusterContent[] = [];

  constructor(config: SunLifecycleConfig, worldCreatedAt: number) {
    this._config = config;
    this._worldCreatedAt = worldCreatedAt;
  }

  get testMode(): boolean { return this._debug.testMode; }
  get entries(): ReadonlyMap<string, SunLifecycleEntry> { return this._entries; }

  init(engine: Engine): void {
    this._engine = engine;
    this._debug.init(engine);
    this._debug.setHandlers(
      () => {
        this._debug.toggleTestMode(() => {
          this._raw.clear();
          this._entries.clear();
          if (this._lastContents.length > 0) this._registerFromContent(this._lastContents);
        }, this._entries);
      },
      () => this._debug.toggleTestFate(this._raw as Map<string, { fate: SunFate }>, this._entries),
    );
    engine.events.on<ClusterContent[]>('sun:stars-available', (contents) => {
      this._lastContents = contents;
      this._registerFromContent(contents);
    });
  }

  private _registerFromContent(contents: ClusterContent[]): void {
    const cfg = this._config;
    const now = Date.now();
    let added = false;
    for (let c = 0; c < contents.length; c++) {
      const stars = contents[c].stars;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        if (!s.entity_id || this._raw.has(s.entity_id)) continue;
        const seed = s.seed ?? 0;
        const fate = seedToFate(seed, cfg.blackHoleChance);
        let createdAt: number, destructionTime: number;
        if (this._debug.testLifetimeSec !== null) {
          const hash = Math.abs(Math.sin(seed * 777.77)) % 1;
          createdAt = now;
          destructionTime = now + this._debug.testLifetimeSec * (0.5 + hash) * 1000;
        } else {
          createdAt = this._worldCreatedAt;
          destructionTime = seedToDestructionTime(seed, this._worldCreatedAt, cfg.minDays, cfg.maxDays);
        }
        const total = Math.max((destructionTime - createdAt) / 1000, 1);
        this._raw.set(s.entity_id, { destructionTime, fate, totalLifetimeSec: total });
        this._entries.set(s.entity_id, {
          entityId: s.entity_id, state: SunState.Alive, fate,
          age: 0, remainingSec: total, scaleMul: 1, destructionTime,
        });
        added = true;
      }
    }
    if (added && this._engine) {
      this._engine.events.emit('sun:lifecycle-update', this._entries);
    }
  }

  registerSuns(suns: ReadonlyArray<SunRegistration>): void {
    for (let i = 0; i < suns.length; i++) {
      const s = suns[i];
      if (this._raw.has(s.entityId)) continue;
      const total = (s.destructionTime - s.createdAt) / 1000;
      this._raw.set(s.entityId, {
        destructionTime: s.destructionTime, fate: s.fate,
        totalLifetimeSec: Math.max(total, 1),
      });
    }
  }

  update(_dt: number): void {
    if (this._raw.size === 0) return;
    const now = Date.now();
    let changed = false;

    for (const [id, raw] of this._raw) {
      const entry = this._entries.get(id);
      if (!entry) continue;
      const remainingSec = (raw.destructionTime - now) / 1000;
      const elapsed = raw.totalLifetimeSec - remainingSec;
      const state = this._calcState(remainingSec, raw.fate);
      const scaleMul = calcLifecycleScale(state, remainingSec, this._config);

      let age: number;
      if (state === SunState.Alive) {
        age = Math.max(0, Math.min(1, elapsed / raw.totalLifetimeSec));
      } else {
        const totalDeathSec = this._config.implodeThresholdSec;
        const deathElapsed = totalDeathSec - remainingSec;
        age = 1.0 + Math.max(0, Math.min(1, deathElapsed / totalDeathSec));
      }

      if (entry.state !== state || Math.abs(entry.scaleMul - scaleMul) > 0.001) changed = true;
      entry.state = state;
      entry.age = age;
      entry.remainingSec = remainingSec;
      entry.scaleMul = scaleMul;
    }

    if (changed && this._engine) {
      this._engine.events.emit('sun:lifecycle-update', this._entries);
    }
  }

  private _calcState(remainingSec: number, fate: SunFate): SunState {
    if (remainingSec > this._config.implodeThresholdSec) return SunState.Alive;
    if (remainingSec > this._config.deathThresholdSec) return SunState.Imploding;
    if (remainingSec > 0) {
      return fate === 'black_hole' ? SunState.CollapsingToBlackHole : SunState.Exploding;
    }
    return fate === 'black_hole' ? SunState.BlackHole : SunState.Dead;
  }

  dispose(): void {
    this._debug.dispose();
    this._entries.clear();
    this._raw.clear();
  }
}
